import { IAddressValidationResult } from "services/api/addresses/dtos/validate-address.response.dto";
import { ThemeColorPalettes } from "theme/theme-colors.theme";

// Address-book carrier validation is a financial company 8 (Connexx USA) feature.
export const ADDRESS_VALIDATION_FINANCIAL_COMPANY = 8;

export const isAddressValidationEnabled = (
  financialCompany?: number | null
): boolean => Number(financialCompany) === ADDRESS_VALIDATION_FINANCIAL_COMPANY;

export interface IParsedValidatedBy {
  checked: boolean;
  ups: boolean;
  dhl: boolean;
}

// Mirrors the server's encoding of connexx_addresses.validated_by.
export const parseValidatedBy = (
  value: string | null | undefined
): IParsedValidatedBy => {
  if (!value) return { checked: false, ups: false, dhl: false };
  const carriers = value.split(",").map((part) => part.trim());
  return {
    checked: true,
    ups: carriers.includes("ups"),
    dhl: carriers.includes("dhl"),
  };
};

export interface IAddressValidationPresentation {
  color: ThemeColorPalettes;
  text: string;
  tooltip: string;
  /** Both carriers confirmed it: the checkmark case. */
  isFullyValidated: boolean;
}

export const getAddressValidationPresentation = (
  value: string | null | undefined
): IAddressValidationPresentation => {
  const { checked, ups, dhl } = parseValidatedBy(value);

  if (!checked) {
    return {
      color: "gray",
      text: "Not checked",
      tooltip: "Not yet validated with UPS or DHL Express",
      isFullyValidated: false,
    };
  }
  if (ups && dhl) {
    return {
      color: "green",
      text: "UPS & DHL",
      tooltip: "Validated by both UPS and DHL Express",
      isFullyValidated: true,
    };
  }
  if (ups) {
    return {
      color: "green",
      text: "UPS",
      tooltip: "Validated by UPS. DHL Express did not confirm this address",
      isFullyValidated: false,
    };
  }
  if (dhl) {
    return {
      color: "green",
      text: "DHL",
      tooltip:
        "Validated by DHL Express. UPS did not confirm this address (UPS only validates US addresses)",
      isFullyValidated: false,
    };
  }
  return {
    color: "red",
    text: "Not validated",
    tooltip: "Neither UPS nor DHL Express could confirm this address",
    isFullyValidated: false,
  };
};

const carrierNote = (
  name: string,
  carrier: IAddressValidationResult["ups"]
): string => `${name}: ${carrier.message || carrier.status}`;

/** One-line toast text for a manual "Validate address" run. */
export const describeAddressValidation = (
  result: IAddressValidationResult
): string => {
  const { ups, dhl } = parseValidatedBy(result.validatedBy);

  if (ups && dhl) return "Validated by UPS and DHL Express";
  if (ups) return `Validated by UPS. ${carrierNote("DHL", result.dhl)}`;
  if (dhl) return `Validated by DHL Express. ${carrierNote("UPS", result.ups)}`;

  const notes = `${carrierNote("UPS", result.ups)}. ${carrierNote("DHL", result.dhl)}`;
  return result.validatedBy
    ? `Address not validated. ${notes}`
    : `Could not validate the address right now. ${notes}`;
};
